/**
 * P8 §S8 — ad click-id capture for server-side attribution (↑ match quality).
 *
 * Captures `fbclid` from the landing URL into a Meta `_fbc` cookie
 * (`fb.1.{ms}.{fbclid}`, per Meta's Conversions API spec) and reads the
 * pixel-set `_fbp` cookie, so the player can forward both as `clickIds` on
 * analytics events. The server later projects them into outbound CAPI payloads.
 *
 * WEB-255: also captures a generic affiliate click_id from common URL params
 * (`click_id`, `sub_id`, `clickid`) — first match wins (last-touch). Stored in
 * the `_aff_click_id` cookie, forwarded as `clickIds.clickId`. Ad-network
 * params (`gclid`, `fbclid`, `ttclid`) are NOT captured here: that cookie feeds
 * the affiliate payout postback (owner's decision 22.09.2026, see the docblock
 * above AFFILIATE_CLICK_ID_PARAMS).
 *
 * TikTok (`ttclid` → `_ttp` formatted cookie) is intentionally deferred — its
 * cookie formula is pending CTO ratification (S9, package `[verify]`). We do
 * NOT write `_ttp`; WEB-2759: the one the TikTok pixel sets itself is READ, like
 * `_fbp`, and forwarded as `clickIds.ttp` (consent-gated by the caller). Since
 * 22.09.2026 `ttclid` is no longer stored under the generic affiliate key
 * either (that cookie feeds affiliate payouts).
 *
 * WEB-2352: ad-network clicks therefore get a last-touch store of their own —
 * the `_ad_click_id` / `_ad_click_param` pair (90 days, written under consent
 * only). It is OURS, not a network format: `_ttp` stays deferred, and `_fbc`
 * keeps serving Meta's pixel. See the docblock above AD_CLICK_ID_COOKIE.
 */

import { readConsentDecision } from './consentCookie';

const FBC_COOKIE = '_fbc';
const FBP_COOKIE = '_fbp';
const FBC_TTL_DAYS = 90;

/** Generic affiliate click_id cookie. Stores raw value, 90-day TTL (last-touch). */
const AFF_CLICK_ID_COOKIE = '_aff_click_id';
const AFF_CLICK_ID_TTL_DAYS = 90;

/**
 * WEB-1168 (П2) — ИМЯ URL-параметра последнего касания (gclid/ttclid/click_id…).
 *
 * `_aff_click_id` хранит только ЗНАЧЕНИЕ: по нему нельзя сказать, из какой сети
 * пришёл клик (у первого касания имя есть — `_ft_click_id.param`, у последнего
 * не было). Пишется ОТДЕЛЬНОЙ кукой, а не сменой формата `_aff_click_id` на
 * JSON: у живых посетителей кука уже лежит в сыром виде, и смена формата
 * потеряла бы им last-touch-атрибуцию. TTL зеркалит значение (90 дней).
 */
const AFF_CLICK_PARAM_COOKIE = '_aff_click_param';

/**
 * WEB-2352 — РЕКЛАМНАЯ МЕТКА ПОСЛЕДНЕГО КАСАНИЯ, СВОЯ КУКА.
 *
 * Зачем отдельная пара, а не место в партнёрской. С 22.09.2026 (решение
 * владельца, свежий взгляд пачки 46) рекламные метки `gclid`/`fbclid`/`ttclid`
 * в партнёрскую куку `_aff_click_id` не пишутся вовсе: по ней считается
 * ВЫПЛАТА ПАРТНЁРУ, и рекламный клик не имеет права её подменять. Но своего
 * хранилища последнего касания у рекламных меток после этого не осталось — у
 * Meta оставалась её собственная `_fbc`, у Google и TikTok не осталось ничего,
 * и метка сети доезжала до `{click_id}` исходящего адреса, только пока жила в
 * адресной строке. На экране воронки, куда посетитель перешёл без сохранения
 * адреса, клиент, льющий из Google Ads, получал переход без клика — молча.
 *
 * Куку первого касания (`_ft_click_id`, 400 дней) подставить вместо этого
 * нельзя: она означает «однажды пришёл по рекламе», а не «этот клик».
 *
 * Устройство — точная копия партнёрской пары: значение и имя параметра лежат
 * ДВУМЯ куками с одним сроком (90 дней, последнее касание, свежий клик
 * перезаписывает). Имя параметра нужно потому, что по значению сеть не
 * различима, а политика о cookie обязана назвать запись поимённо.
 *
 * ⚠ Пишется ТОЛЬКО под согласием — тем же единственным путём, что
 * партнёрская: из `captureClickIds()`, которую зовут только после «Принять»
 * (`trackEvent.ts`, `euConsent.ts`). Снимается отказом вместе с остальным
 * атрибуционным следом (`ATTRIBUTION_COOKIES` в `euConsent.ts`).
 */
const AD_CLICK_ID_COOKIE = '_ad_click_id';
const AD_CLICK_PARAM_COOKIE = '_ad_click_param';
const AD_CLICK_ID_TTL_DAYS = 90;

/**
 * WEB-841 (батч Алекса E1) — идентификатор рекламного клика ПЕРВОГО касания.
 * JSON {param, value}: имя URL-параметра (откуда пришёл) + значение. Пишется
 * ОДИН раз и никогда не перезаписывается (в отличие от last-touch кук выше):
 * первое касание задним числом не восстановить. 400 дней = потолок Chrome.
 */
const FT_CLICK_ID_COOKIE = '_ft_click_id';
const FT_CLICK_ID_TTL_DAYS = 400;

/** Все известные клик-параметры для first-touch: сети + партнёрки. */
const FIRST_TOUCH_CLICK_PARAMS: readonly string[] = [
  'gclid',
  'fbclid',
  'ttclid',
  'click_id',
  'sub_id',
  'clickid',
];

/**
 * URL params checked in priority order for generic affiliate click_id.
 * First param present in the URL wins; existing cookie persists when none match.
 */
/**
 * 🔴 РЕВИЗИЯ ПАЧКИ 46 (Н3), 22.09.2026 — `fbclid` ИЗ ЭТОГО СПИСКА УБРАН.
 *
 * Короткая история. 22.09 владелец решил (вариант 1): метка `{click_id}`
 * исходящего адреса — это клик ЛЮБОЙ сети, включая Meta; отдельной метки
 * `{fbclid}` не заводим. Выполнено это было дописыванием `fbclid` в конец
 * ЭТОГО списка — а список кормит не только адрес: из него заполняется кука
 * `_aff_click_id` → поле события `clickIds.clickId`, по которому уходит
 * ПОСТБЭК ПАРТНЁРКЕ (`public-event-side-effects.service.ts`, `macros.click_id`)
 * и пишется стадия «Клик» воронки. То есть решение про адрес молча поменяло
 * деньги партнёра: посетитель из Meta без партнёрской метки стал уезжать в
 * постбэк с `click_id = fbclid` (раньше — пусто), а повторный заход из Meta
 * перетирал партнёрскую метку прежнего захода.
 *
 * Поэтому здесь восстановлено прежнее правило WEB-2185: рекламная метка НЕ
 * попадает в партнёрскую куку. Решение владельца при этом выполнено
 * по-прежнему, но НА МЕСТЕ РАСКРЫТИЯ МАКРОСА: `resolveOutboundClickIdMacro()`
 * ниже подставляет в `{click_id}` партнёрский клик, а если его нет вовсе —
 * метку рекламной сети (из адреса входа или, у Meta, из своей `_fbc`).
 * Клиент, льющий из рекламы, получает клик в адресе; партнёр получает свою
 * выплату. Второй конец того же правила — `LAST_TOUCH_CLICK_ID_PARAMS` на
 * сервере (`backend/src/domains/utils/smartlink-outbound-url.ts`), сторож
 * сравнивает ПОСЛЕДОВАТЕЛЬНОСТИ обоих списков.
 *
 * 🔴 РЕШЕНИЕ ВЛАДЕЛЬЦА 22.09.2026 (свежий взгляд пачки 46, находка 1): тем же
 * правилом из списка убраны `gclid` и `ttclid`. Довод к ним относится слово в
 * слово, а стояли они ВПЕРЕДИ партнёрского имени `clickid` — то есть заход
 * `?clickid=PARTNER&gclid=G` уводил в куку выплат метку Google. Теперь в
 * денежном списке рекламных меток нет ни одной (сторожа сверяют его с
 * реестром `AD_NETWORK_CLICK_PARAMS` множеством, а не по одному имени).
 *
 * Порядок внутри списка — приоритет («первый найденный выигрывает»).
 */
const AFFILIATE_CLICK_ID_PARAMS: readonly string[] = [
  'click_id',
  'sub_id',
  'clickid',
];

/**
 * ПОРЯДОК ЗАПАСНЫХ ЗНАЧЕНИЙ метки `{click_id}` — метки рекламных сетей,
 * которые подставляются в адрес ухода, когда партнёрской метки нет вовсе.
 *
 * Последовательность — зеркало реестра сетей на сервере
 * (`AD_NETWORK_CLICK_PARAMS` в `backend/src/analytics/utils/ad-click-params.util.ts`)
 * и второй конец серверного `AD_NETWORK_FALLBACK_PARAMS`. Сверяет обе
 * последовательности с реестром сторож `smartlink-outbound-url.web2123.spec.ts`:
 * разъехавшийся порядок — это ровно тот дефект, из-за которого один и тот же
 * заход давал разные значения на двух ветках ухода.
 */
const AD_NETWORK_FALLBACK_PARAMS: readonly string[] = [
  'gclid',
  'fbclid',
  'ttclid',
];

/**
 * WEB-1471 — пределы длины, зеркало приёмной стороны
 * (backend/src/analytics/dto/track-event.dto.ts: clickId 512, ftValue 255,
 * clickIdParam/ftParam 64, fbc/fbp 255).
 *
 * Зачем предохранитель на фронте: глобальная проверка входных данных включена
 * в строгом режиме (backend/src/main.ts, whitelist + forbidNonWhitelisted),
 * поэтому превышение длины ВНУТРИ вложенного объекта clickIds отбивает ВЕСЬ
 * запрос события 400-й — теряется не поле, а всё событие. А так как значение
 * лежит в куке 90 дней (первое касание 400), ломались и все следующие события
 * того же посетителя: у клиента по этому источнику трафика ноль аналитики на
 * месяцы вперёд.
 *
 * Тот же приём уже стоит в соседнем модуле UTM-меток
 * (utmCapture.ts:44,85 — MAX_VALUE_LENGTH + sanitize, шрам WEB-933 п.3);
 * здесь класс дефекта был признан, но не закрыт.
 *
 * ⚠ Пределы на сервере этим атомом НЕ меняются (граница карточки WEB-1471).
 * Меняются здесь — только если поменяются @MaxLength в DTO.
 */
const MAX_CLICK_ID_LENGTH = 512;
const MAX_FT_VALUE_LENGTH = 255;
const MAX_PARAM_LENGTH = 64;
/** fbc/fbp у Meta ограничены 255 — длинный fbclid отбил бы событие так же. */
const MAX_META_LENGTH = 255;

/**
 * Обрезка по образцу utmCapture.sanitize(). Режем НАЧАЛО значения (первые N
 * символов), а не хвост: у партнёрских меток значащая часть идёт первой.
 * Обрезанная метка хуже целой, но целое потерянное событие — хуже обеих.
 */
function sanitize(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

/**
 * WEB-2356 — МЕТКА ДЛИННЕЕ ПОТОЛКА ЛОЖНАЯ, А НЕ ОБРЕЗАННАЯ.
 *
 * ЧТО БЫЛО СЛОМАНО. Потолок длины метки клика (512 — предел поля `clickId`
 * приёмной стороны событий) две ветки ухода понимали по-разному: сервер
 * (`resolveLastTouchClickIdFromQuery`) слишком длинное значение ПРОПУСКАЕТ и
 * идёт дальше по списку, а браузер ОБРЕЗАЛ его до потолка и подставлял.
 * Один и тот же заход `?click_id=<600 символов>&gclid=G1` давал у плеера
 * обрезанную партнёрскую метку, а у смарт-ссылки — `G1`.
 *
 * РЕШЕНИЕ (оркестратор, 22.09.2026): обе стороны ведут себя как сервер.
 * Обрезанная метка — это уже НЕ ТОТ клик: партнёр его у себя не найдёт, а
 * чужой сервер примет за настоящий и запишет конверсию не тому. Пустое место
 * честнее подделки. Поэтому слишком длинное значение не подставляется, не
 * пишется в куки и не считается «партнёрской меткой» — разбор идёт дальше
 * ровно так, как если бы её не было вовсе.
 *
 * ⚠ ГРАНИЦА, названная прямо. Обрезка `sanitize` выше НЕ отменяется: она
 * по-прежнему стоит на пути метки в ТЕЛО СОБЫТИЯ (`getStoredClickIds`,
 * `getLastTouchClickId`, первое касание) — там у неё другая забота, из шрама
 * WEB-1471: длинное значение в куке живого посетителя отбивало бы ВСЁ событие
 * 400-й, и он терялся из аналитики на месяцы. Здесь же речь про значение,
 * которое уезжает на ЧУЖОЙ сервер, и цена ошибки обратная.
 *
 * Общая таблица случаев на обе ветки —
 * `backend/src/shared/utils/outbound-click-id-order.cases.json`.
 */
function withinCap(value: string | undefined, max: number): string | undefined {
  if (value === undefined) return undefined;
  return value.length > max ? undefined : value;
}

function readCookie(name: string): string | undefined {
  try {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + name + '=([^;]*)'),
    );
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * WEB-1471 п.4: читаем тоже с обрезкой. У живых посетителей длинные значения
 * УЖЕ лежат в браузере (до 400 дней), и починка «только на записи» их события
 * не спасла бы — кабинет продолжал бы терять аналитику по этим визитам.
 */
function readCookieCapped(name: string, max: number): string | undefined {
  const value = readCookie(name);
  return value === undefined ? undefined : sanitize(value, max);
}

/**
 * WEB-2292 п.6 — ВЫБОР ОДНОЙ ИЗ ДВУХ КУК `_fbc`. Решение и довод записаны
 * здесь, рядом с кодом, как просила карточка.
 *
 * Что происходит в браузере. Свою `_fbc` мы пишем БЕЗ атрибута `domain` —
 * то есть только на текущем хосте (`<воронка>.<апекс>`), а
 * библиотека пикселя Meta ставит свою на домен второго уровня
 * (`.<апекс>`, на бою `.subster.ai`). Это две РАЗНЫЕ куки с одинаковым именем, и в
 * `document.cookie` они неотличимы: атрибута домена там нет вовсе. При равном
 * пути порядок задаётся временем создания, поэтому чтение «первое совпадение
 * регуляркой» брало то одну, то другую — браузерное и серверное события
 * одного визита могли уехать с разными метками клика.
 *
 * Из трёх выходов карточки выбран ТРЕТИЙ — «читать строго свою», потому что
 * у него наименьший радиус:
 *  · «не писать свою, если её поставил пиксель» — на момент захвата пикселя
 *    может ещё не быть (он грузится асинхронно), и тогда мы остались бы без
 *    метки вовсе;
 *  · «писать на домен второго уровня» — это расширение своего следа на все
 *    соседние воронки апекса, то есть правовой периметр шире нужного (тот же
 *    довод, по которому доменная кука согласия включается только клиентом,
 *    WEB-2140).
 *
 * Правило выбора детерминированное и от места в строке не зависит:
 *  1) значение, чей клик совпадает с `fbclid` ТЕКУЩЕГО адреса, — это заведомо
 *     метка этого визита (наша: чужая могла остаться от прошлого);
 *  2) иначе — самая свежая по метке времени `fb.1.<ms>.…` (на следующих
 *     экранах воронки `fbclid` в адресе уже нет);
 *  3) значения чужого формата берутся только когда разобрать нечего.
 *
 * Обрезка WEB-1471 остаётся в силе — она применяется к выбранному значению.
 *
 * ⚠ ЧЕСТНАЯ ГРАНИЦА (ревизия 46, Н4). Имя «свою» обещает больше, чем можно
 * выполнить: библиотека пикселя Meta пишет свою `_fbc` ИЗ ТОГО ЖЕ `fbclid`,
 * значит правилу (1) удовлетворяют ОБЕ куки, и отличить нашу от чужой в
 * `document.cookie` НЕЧЕМ по построению — атрибута домена там нет. Что
 * правило даёт на самом деле: (а) выбор не зависит от порядка кук в строке,
 * то есть от прогрузки пикселя; (б) при равном происхождении значения
 * одинаковы, и какая из двух выбрана — безразлично; (в) чужая метка ПРОШЛОГО
 * визита проигрывает метке текущего. Этого достаточно, чтобы браузерное и
 * серверное события одного визита уехали с ОДНОЙ меткой, — а именно это и
 * чинилось.
 *
 * Там же (Н4): у правила (1) предпочитается ТОЧНОЕ равенство, и пустой клик
 * в битой куке (`fb.1.123.`) совпадением больше не считается — иначе обрывок
 * выигрывал у целого значения, потому что `startsWith('')` истинно всегда.
 */
function readOwnFbcCookie(): string | undefined {
  let raw: string;
  try {
    raw = document.cookie;
  } catch {
    return undefined;
  }
  const values: string[] = [];
  for (const m of raw.matchAll(/(?:^|; )_fbc=([^;]*)/g)) {
    try {
      values.push(decodeURIComponent(m[1]));
    } catch {
      values.push(m[1]);
    }
  }
  if (values.length <= 1) return values[0];

  const parsed = values
    .map((value) => {
      const m = /^fb\.1\.(\d+)\.(.*)$/.exec(value);
      return m ? { value, ms: Number(m[1]), click: m[2] } : undefined;
    })
    .filter((v): v is { value: string; ms: number; click: string } => !!v);
  if (parsed.length === 0) return values[0];

  let urlFbclid: string | null = null;
  try {
    urlFbclid = new URLSearchParams(window.location.search).get('fbclid');
  } catch {
    urlFbclid = null;
  }
  if (urlFbclid) {
    // Сначала — точное равенство: целое значение всегда лучше обрывка.
    const exact = parsed.filter((p) => p.click === urlFbclid);
    if (exact.length > 0) {
      return exact.reduce((a, b) => (b.ms >= a.ms ? b : a)).value;
    }
    // Наше значение собрано из САМОГО fbclid, но могло быть обрезано по
    // пределу Meta (255) — поэтому дальше сверяем началом. Пустой клик
    // совпадением НЕ считается (ревизия 46, Н4): `startsWith('')` истинно
    // всегда, и битая кука `fb.1.123.` выигрывала у целой.
    const prefixed = parsed.filter(
      (p) => p.click.length > 0 && urlFbclid.startsWith(p.click),
    );
    if (prefixed.length > 0) {
      return prefixed.reduce((a, b) => (b.ms >= a.ms ? b : a)).value;
    }
  }
  return parsed.reduce((a, b) => (b.ms >= a.ms ? b : a)).value;
}

function writeCookie(name: string, value: string, days: number): void {
  try {
    const expires = new Date(Date.now() + days * 86_400_000).toUTCString();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
  } catch {
    /* ignore — cookie write best-effort */
  }
}

/**
 * Capture ad click-ids from the current URL:
 * - `fbclid` → `_fbc` cookie (Meta format `fb.1.{ms}.{fbclid}`, last-touch).
 * - generic affiliate params → `_aff_click_id` cookie (raw value, last-touch).
 *
 * Safe to call on every page load — missing params leave existing cookies intact.
 */
export function captureClickIds(): void {
  try {
    const params = new URLSearchParams(window.location.search);

    // Meta _fbc (last-touch — overwrite on fresh fbclid).
    const fbclid = params.get('fbclid');
    if (fbclid) {
      // WEB-1471: режем СОБРАННОЕ значение, а не сам fbclid — обязательный
      // префикс `fb.1.{ms}.` при этом цел, режется только хвост метки.
      writeCookie(
        FBC_COOKIE,
        sanitize(`fb.1.${Date.now()}.${fbclid}`, MAX_META_LENGTH),
        FBC_TTL_DAYS,
      );
    }

    // Generic affiliate click_id (last-touch — first matched param wins per call).
    for (const param of AFFILIATE_CLICK_ID_PARAMS) {
      // WEB-2356: значение длиннее потолка — ложная метка, а не повод писать
      // огрызок: перебор идёт ДАЛЬШЕ по списку (так же, как на сервере), и
      // заход `?click_id=<600>&sub_id=S1` даёт партнёру S1, а не обрезок.
      const value = withinCap(
        params.get(param) ?? undefined,
        MAX_CLICK_ID_LENGTH,
      );
      if (value) {
        writeCookie(AFF_CLICK_ID_COOKIE, value, AFF_CLICK_ID_TTL_DAYS);
        // WEB-1168: имя параметра-источника — рядом со значением и одним
        // сроком жизни, чтобы пара не разъезжалась при перезаписи.
        writeCookie(
          AFF_CLICK_PARAM_COOKIE,
          sanitize(param, MAX_PARAM_LENGTH),
          AFF_CLICK_ID_TTL_DAYS,
        );
        break;
      }
    }

    // WEB-2352: рекламная метка последнего касания — в СВОЮ пару кук. Порядок
    // перебора — порядок реестра сетей (`AD_NETWORK_FALLBACK_PARAMS`), тот же,
    // по которому метка подставляется в `{click_id}` из адреса: иначе заход
    // сразу с двумя метками давал бы в адресе одну сеть, а на следующем экране
    // другую. Партнёрскую куку это не трогает — списки разные намеренно.
    for (const param of AD_NETWORK_FALLBACK_PARAMS) {
      // WEB-2356: тот же потолок и то же правило, что у партнёрской пары, —
      // слишком длинная метка сети ложная, перебор идёт к следующей сети.
      const value = withinCap(
        params.get(param) ?? undefined,
        MAX_CLICK_ID_LENGTH,
      );
      if (value) {
        writeCookie(AD_CLICK_ID_COOKIE, value, AD_CLICK_ID_TTL_DAYS);
        writeCookie(
          AD_CLICK_PARAM_COOKIE,
          sanitize(param, MAX_PARAM_LENGTH),
          AD_CLICK_ID_TTL_DAYS,
        );
        break;
      }
    }

    // WEB-841: first-touch — пишем ТОЛЬКО если куки ещё нет (AC4: повторные
    // визиты с другой меткой или без метки первое касание не перетирают).
    if (!readCookie(FT_CLICK_ID_COOKIE)) {
      for (const param of FIRST_TOUCH_CLICK_PARAMS) {
        // WEB-2361 — ПОТОЛОК ПЕРВОГО КАСАНИЯ: 255 (предел `ftValue` приёмной
        // стороны), и слишком длинная метка ЛОЖНАЯ, а не обрезаемая.
        //
        // Правило то же, что у последнего касания (WEB-2356), и второй его
        // конец — сервер: `extractClickIdFromQuery`
        // (`backend/src/domains/utils/smartlink-outbound-url.ts`) метку
        // длиннее `MAX_FIRST_TOUCH_VALUE_LENGTH` пропускает и идёт дальше по
        // списку. Пока браузер её обрезал, один и тот же заход давал у
        // смарт-ссылки «клика не было», а у плеера — обрезок, который живёт
        // 400 ДНЕЙ и все эти месяцы называет посетителя пришедшим по клику,
        // которого у сети нет. Здесь цена ошибки выше всего именно из-за
        // срока, поэтому пустое место честнее подделки.
        //
        // Обрезка на ЧТЕНИИ (`getFirstTouchClickId`) остаётся: у живых
        // посетителей длинная кука уже лежит, и без неё их события отбивались
        // бы 400-й целиком (шрам WEB-1471 п.4).
        const value = withinCap(
          params.get(param) ?? undefined,
          MAX_FT_VALUE_LENGTH,
        );
        if (value) {
          writeCookie(
            FT_CLICK_ID_COOKIE,
            JSON.stringify({
              param: sanitize(param, MAX_PARAM_LENGTH),
              value,
            }),
            FT_CLICK_ID_TTL_DAYS,
          );
          break;
        }
      }
    }
  } catch {
    /* ignore */
  }
}

export interface ClickIds {
  fbc?: string;
  fbp?: string;
  /** WEB-2759 — кука `_ttp` пикселя TikTok (читаем, не пишем). */
  ttp?: string;
  /** Generic affiliate click_id captured from URL (WEB-255 S2S). */
  clickId?: string;
  /**
   * WEB-1168 (П2, доделка после возврата QA 16.08): имя URL-параметра
   * ПОСЛЕДНЕГО касания (gclid/ttclid/click_id/...), рядом со значением
   * `clickId`. Отсутствует у посетителей со старой кукой `_aff_click_id`
   * без имени (param до этого атома не писался) — значение при этом цело.
   */
  clickIdParam?: string;
  /** WEB-841: имя URL-параметра клика первого касания (gclid/fbclid/...). */
  ftParam?: string;
  /** WEB-841: значение клика первого касания. */
  ftValue?: string;
}

/** WEB-841: первое касание {param, value} или null (метки не было). */
export function getFirstTouchClickId(): {
  param: string;
  value: string;
} | null {
  const raw = readCookie(FT_CLICK_ID_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { param?: unknown; value?: unknown };
    if (typeof parsed.param === 'string' && typeof parsed.value === 'string') {
      // WEB-1471 п.4: кука могла быть записана до этой починки (400 дней) —
      // обрезаем на чтении, иначе событие отобьётся 400-й.
      return {
        param: sanitize(parsed.param, MAX_PARAM_LENGTH),
        value: sanitize(parsed.value, MAX_FT_VALUE_LENGTH),
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * WEB-1168 (П2): последнее касание — значение клика и, если известно, имя
 * URL-параметра, из которого оно пришло (= рекламная сеть). `null`, когда
 * метки не было.
 *
 * `param` НЕОБЯЗАТЕЛЕН намеренно: у посетителей, чья кука `_aff_click_id`
 * записана до этого атома, имени нет — значение всё равно отдаём.
 *
 * Едет в тело аналитического события через getStoredClickIds() как
 * `clickIdParam` (ClickIdsDto на бэке объявляет поле в этом же атоме —
 * доделка после возврата QA 16.08, раньше отбилось бы forbidNonWhitelisted,
 * шрам WEB-933 п.3).
 */
export function getLastTouchClickId(): {
  param?: string;
  value: string;
} | null {
  // WEB-1471 п.4: обрезка и на чтении — у посетителя может лежать кука,
  // записанная до починки.
  const value = readCookieCapped(AFF_CLICK_ID_COOKIE, MAX_CLICK_ID_LENGTH);
  if (!value) return null;
  const param = readCookieCapped(AFF_CLICK_PARAM_COOKIE, MAX_PARAM_LENGTH);
  return param ? { param, value } : { value };
}

/**
 * WEB-2352 — РЕКЛАМНЫЙ клик последнего касания: значение и, если известно, имя
 * параметра (= сеть). `null`, когда рекламной метки не было.
 *
 * Пара читается целиком, а не одним значением, потому что имя сети — это ровно
 * то, чего не хватает партнёрской куке (`getLastTouchClickId` объясняет, почему
 * у старых посетителей имени нет). Само раскрытие метки `{click_id}` берёт
 * отсюда только значение: в адрес ухода уезжает клик, а не название сети.
 */
export function getLastTouchAdClickId(): {
  param?: string;
  value: string;
} | null {
  // WEB-2356: у ЭТОЙ куки читаем без обрезки — значение отсюда уезжает в
  // адрес на чужой сервер, а не в тело нашего события. Слишком длинное
  // (чужой рукой поставленное) значение считается отсутствующим.
  const value = withinCap(readCookie(AD_CLICK_ID_COOKIE), MAX_CLICK_ID_LENGTH);
  if (!value) return null;
  const param = readCookieCapped(AD_CLICK_PARAM_COOKIE, MAX_PARAM_LENGTH);
  return param ? { param, value } : { value };
}

/**
 * Ревизия 46 (Н3) — ЗНАЧЕНИЕ МЕТКИ `{click_id}` ДЛЯ ИСХОДЯЩЕГО АДРЕСА.
 *
 * Решение владельца 22.09 («`{click_id}` — клик любой сети, включая Meta»)
 * живёт ЗДЕСЬ, на месте раскрытия макроса, а не в списке захвата: из списка
 * захвата заполняется кука `_aff_click_id`, а по ней считаются ВЫПЛАТЫ
 * ПАРТНЁРУ (постбэк и стадия «Клик»), и рекламной метке там не место.
 *
 * Порядок:
 *  1) ПАРТНЁРСКИЙ клик последнего касания (кука `_aff_click_id`) — если он
 *     есть, поведение ровно прежнее, ничего не меняется;
 *  2) метка рекламной сети из ТЕКУЩЕГО адреса, по порядку реестра
 *     (`AD_NETWORK_FALLBACK_PARAMS`: `gclid` → `fbclid` → `ttclid`) — на
 *     странице входа это целое значение, не обрезанное по пределу Meta (255);
 *     ТОЛЬКО под решением «Принять» (свежий взгляд пачки 46, находка 1 —
 *     см. гейт в теле);
 *  3) WEB-2352 — своя кука рекламного клика `_ad_click_id`: на следующих
 *     экранах воронки адреса входа уже нет, а клик остаётся нужен;
 *  4) `fbclid` из куки `_fbc` — формат Meta, остаётся последним запасным для
 *     посетителей, чья `_ad_click_id` ещё не записана (кука новая, живые
 *     визиты с прошлых версий про неё не знают).
 *
 * На КАЖДОМ шаге действует потолок длины (WEB-2356): значение длиннее 512 —
 * ложная метка, шаг считается пустым и разбор идёт дальше, как на сервере.
 *
 * ⚠ ЧЕСТНАЯ ГРАНИЦА. Шаги 3 и 4 работают только там, где посетитель дал
 * согласие: обе куки пишутся под «Принять» и удаляются при отказе. Кука
 * первого касания (`_ft_click_id`, 400 дней) сюда по-прежнему НЕ подставляется
 * — она означает «однажды пришёл по рекламе», а не «этот клик».
 *
 * Второй конец на сервере — `resolveLastTouchClickIdFromQuery`
 * (`backend/src/domains/utils/smartlink-outbound-url.ts`): у ветки смартлинка
 * тот же порядок и то же запасное значение.
 */
export function resolveOutboundClickIdMacro(): string | undefined {
  // WEB-2356: здесь кука читается БЕЗ обрезки, в отличие от чтения для тела
  // события (`getLastTouchClickId`): обрезанное значение в адресе — подделка
  // клика на чужом сервере, а обрезанное значение в нашем событии — всего
  // лишь спасение события от отказа 400 (шрам WEB-1471). Разные заботы —
  // разные правила, и обе написаны, а не подразумеваются.
  const partner = withinCap(
    readCookie(AFF_CLICK_ID_COOKIE),
    MAX_CLICK_ID_LENGTH,
  );
  if (partner) return partner;

  // СВЕЖИЙ ВЗГЛЯД ПАЧКИ 46 (находка 1): адрес спрашивается ТОЛЬКО под
  // «Принять». Обе куки выше и ниже пишет `captureClickIds()`, а её зовут
  // только после согласия (`trackEvent.ts`, `euConsent.ts`), — адресная
  // строка же доступна всегда, и без этого гейта рекламный идентификатор
  // клика уезжал на чужой сервер у европейца, который просто не тронул
  // нижнюю плашку баннера (отсутствие решения — не отказ, и общий гейт
  // плеера его пропускает). Решение читает тот же единственный помощник,
  // что и постоянный идентификатор посетителя, — второй копии гейта не
  // заводим (шрам WEB-953).
  if (readConsentDecision() === 'granted') {
    try {
      const params = new URLSearchParams(window.location.search);
      for (const param of AD_NETWORK_FALLBACK_PARAMS) {
        // WEB-2356: длиннее потолка — ложная метка, идём к следующей сети
        // (ровно как сервер), а не подставляем обрезок.
        const value = withinCap(
          params.get(param) ?? undefined,
          MAX_CLICK_ID_LENGTH,
        );
        if (value) return value;
      }
    } catch {
      /* адрес прочитать не удалось — остаётся кука */
    }
  }

  // WEB-2352: своя кука рекламного клика — ПОСЛЕ адреса и ДО `_fbc`.
  //  · после адреса: в адресе лежит клик ТЕКУЩЕГО захода, в куке — последнего,
  //    который мог быть и прошлым; переставь их местами, и возврат по новому
  //    объявлению уезжал бы со старым кликом;
  //  · до `_fbc`: там значение в формате Meta и только для Meta, а здесь —
  //    клик любой из трёх сетей ровно тем значением, каким пришёл в адресе.
  // Гейта согласия здесь нет и не нужно: куку пишет только `captureClickIds()`
  // под «Принять», а отказ её удаляет (`clearAttributionStorage`). Второй гейт
  // на том же решении был бы второй копией правила (шрам WEB-953).
  const adCookie = getLastTouchAdClickId();
  if (adCookie) return adCookie.value;

  const fbc = readOwnFbcCookie();
  // WEB-2356 — `_fbc` НА ПРЕДЕЛЕ СВОЕГО ФОРМАТА ОТДАЁТ ОБРЕЗАННЫЙ КЛИК.
  //
  // Кука Meta собирается как `fb.1.<ms>.<клик>` и режется по 255 — это предел
  // самой Meta, и менять его нельзя, иначе сломается пиксель. Но клик внутри
  // такой куки при длинном `fbclid` УЖЕ обрезан, а правило этой карточки
  // ровно про это: обрезанная метка ложная. Признак обрезки один и надёжный —
  // кука лежит ровно на своём пределе. Редкий честный `fbclid`, давший ровно
  // 255 символов, мы при этом теряем; ошибаться в сторону пустого места здесь
  // правильнее, чем в сторону подделки клика на чужом сервере.
  //
  // Без этого шага одна и та же ссылка `?fbclid=<700 символов>` давала у
  // смарт-ссылки пусто (сервер метку такой длины пропускает), а у плеера —
  // обрывок из `_fbc`: та же расхождение двух веток, ради которой карточка и
  // заведена.
  if (fbc !== undefined && fbc.length >= MAX_META_LENGTH) return undefined;
  const fromCookie = fbc ? /^fb\.1\.\d+\.(.+)$/.exec(fbc)?.[1] : undefined;
  // Правило потолка одно на все источники этой функции. На своей `_fbc` оно
  // не срабатывает никогда (её предел меньше), но оставлять здесь
  // единственную обрезку значит оставить лазейку для чужой куки того же имени.
  return withinCap(fromCookie, MAX_CLICK_ID_LENGTH);
}

/** Read the captured click-id cookies (omits absent keys). */
export function getStoredClickIds(): ClickIds {
  const out: ClickIds = {};
  // WEB-1471: последний рубеж перед телом события — каждое поле не длиннее
  // своего @MaxLength в ClickIdsDto. `_fbp` пишем не мы (пиксель Meta),
  // поэтому его длину тоже не считаем гарантированной.
  // WEB-2292 п.6: своя `_fbc`, а не первое совпадение в строке кук.
  const ownFbc = readOwnFbcCookie();
  const fbc =
    ownFbc === undefined ? undefined : sanitize(ownFbc, MAX_META_LENGTH);
  const fbp = readCookieCapped(FBP_COOKIE, MAX_META_LENGTH);
  const clickId = readCookieCapped(AFF_CLICK_ID_COOKIE, MAX_CLICK_ID_LENGTH);
  if (fbc) out.fbc = fbc;
  if (fbp) out.fbp = fbp;
  // WEB-2759: `_ttp` тоже ставит не мы (пиксель TikTok); потолок 512 — как у
  // `clickIds.ttp` на сервере. Сбор под согласием решает вызывающий (trackEvent).
  const ttp = readCookieCapped('_ttp', MAX_CLICK_ID_LENGTH);
  if (ttp) out.ttp = ttp;
  if (clickId) {
    out.clickId = clickId;
    // WEB-1168 (П2): имя параметра-источника last-touch едет рядом со
    // значением, если известно (старые куки без имени его не несут).
    const clickIdParam = readCookieCapped(
      AFF_CLICK_PARAM_COOKIE,
      MAX_PARAM_LENGTH,
    );
    if (clickIdParam) out.clickIdParam = clickIdParam;
  }
  // WEB-841: first-touch едет на каждое событие → доживает до оплаты (AC3).
  const ft = getFirstTouchClickId();
  if (ft) {
    out.ftParam = ft.param;
    out.ftValue = ft.value;
  }
  return out;
}
